import type { Sample } from '@ctcells/contract'

import type { SampleGroupShape } from '../recipes/recipeForm.ts'

import type { GroupAssignment } from './patientChambers.ts'

/**
 * **시료 ↔ 레시피 묶음 — 서버 값으로 잇는다**(2026-09-28 · BR-31 되살리기).
 *
 * 🔴 **서버가 이제 이 축을 준다.** 배치를 `assignments` 와 함께 만들면 시료마다 `assigned_well`(불변)이 서고,
 *    서버가 그 웰을 담은 묶음의 표지를 `sample_ref` 로 **파생해** 싣는다(`GET /samples` · `services/samples.
 *    sample_refs_for`). 실측(2026-09-28 · 작업 `10b71573`): 다섯 시료 전부 `assigned_well`(A1·B1·A3·C1·C3) ·
 *    `sample_ref`(P1~P4 · REF)가 왔다. 같은 대응이 `Batch.sample_assignments` 에도 있다.
 *
 * 규칙 — 묶음마다
 *   ① 그 묶음의 `sample_ref` 와 같은 `sample_ref` 를 가진 시료
 *   ② 없으면 그 묶음의 웰 중 하나가 `assigned_well` 인 시료
 *   ③ 둘 다 없으면 비운다(「미배정」) — 🔴 **대체 규칙으로 메우지 않는다.** 서버가 배정을 준 작업에서 빈 묶음은 사실이다
 *
 * ⚠ **배정 없이 만든 작업**(`assignments` 를 안 준 배치 · 옛 회차)은 두 칸이 전부 비어 온다 — 그때만 `undefined` 를
 *   돌려주고, 부르는 쪽이 **임시 규칙**(`temporarySampleGroups` · 표지된 목)으로 떨어진다.
 */
export function serverSampleGroups(samples: readonly Sample[]): GroupAssignment | undefined {
  const told = samples.some((sample) => sample.sample_ref != null || sample.assigned_well != null)
  if (!told) return undefined
  return (groups) =>
    groups.map((group) => {
      const byRef =
        group.sampleRef === undefined
          ? undefined
          : samples.find((sample) => sample.sample_ref === group.sampleRef)
      const byWell =
        byRef ??
        samples.find(
          (sample) => sample.assigned_well != null && group.wells.includes(sample.assigned_well),
        )
      return byWell?.sample_id
    })
}

/**
 * **묶음에 드는 시료 전부** — 서버 값(`sample_ref` 가 묶음 표지와 같거나 `assigned_well` 이 묶음 웰)으로 고른다.
 *
 * 🔴 왜 — v0.9.3 은 **시료 1건 = 챔버 1개**라 한 환자 묶음(`A1`·`B1`)에 시료가 둘 든다(개발용 버튼 「환자 4 × 챔버 2 +
 *    대조 1」 · eric 2026-10-07). 묶음 카드는 대표 시료 하나(`serverSampleGroups`)로 그리고, 나머지는 **그 카드의 다른
 *    챔버**다 — 따로 「챔버 배정 없음」 카드로 세우지 않게 `patientChambers` 에 넘긴다.
 */
export function groupedSampleIds(
  samples: readonly Sample[],
  groups: readonly SampleGroupShape[],
): ReadonlySet<string> {
  const ids = new Set<string>()
  for (const sample of samples) {
    const inGroup = groups.some(
      (group) =>
        (group.sampleRef !== undefined && sample.sample_ref === group.sampleRef) ||
        (sample.assigned_well != null && group.wells.includes(sample.assigned_well)),
    )
    if (inGroup) ids.add(sample.sample_id)
  }
  return ids
}

/**
 * **배정만으로 잇는다** — 시료 응답(`GET /samples`)을 못 받았을 때(viewer 403). `Batch.sample_assignments`
 * (웰 ↔ 시료 번호)는 viewer 도 받는다. 묶음의 웰 중 하나에 배정된 시료가 그 묶음의 것이다.
 * 🔴 모니터링(`useSampleFlow`)과 작업 상세 시료 표가 **같은 함수**를 쓴다 — 둘이 다른 짝을 말하지 않게.
 */
export function assignmentSampleGroups(
  assignments: readonly { wellName: string; sample_id: string }[],
): GroupAssignment {
  return (groups) =>
    groups.map((group) => assignments.find((a) => group.wells.includes(a.wellName))?.sample_id)
}

/**
 * **시료 → 그 시료의 챔버(웰) 전부** — 모니터링 카드가 시료 하나에 챔버 둘(`A1` · `A2`)을 그리는 그 짝이다
 * (`patientChambers` 가 묶음 `i` 의 `wells` 를 `assign` 이 준 `i` 번째 시료에 붙인다 — 여기도 똑같이 붙인다).
 *
 * 🔴 **짝은 레시피가 정한다**(`tray_path.sample_groups[].wells`). 웰 이름으로 셈하지 않는다(「A1 옆은 A2」 같은 규칙 없음) —
 *    실물 레시피의 셋째 환자는 `A3 · B3` 이다.
 * 🔴 묶음에 못 든 시료는 맵에 없다. 부르는 쪽이 서버 배정 웰 하나로 떨어지거나 비운다 — 여기서 지어내지 않는다.
 */
export function sampleWellsOf(
  groups: readonly SampleGroupShape[],
  assign: GroupAssignment | undefined,
): Map<string, readonly string[]> {
  const wells = new Map<string, readonly string[]>()
  if (assign === undefined) return wells
  const assigned = assign(groups, [])
  groups.forEach((group, index) => {
    const sampleId = assigned[index]
    if (sampleId !== undefined && !wells.has(sampleId)) wells.set(sampleId, group.wells)
  })
  return wells
}
